import type { Fee, Offer } from '@psp-aggregator/sdk';

import { isOfferLegKey, OFFER_LEG_KEYS, type OfferLegKey } from '../model/legs';

export const FIXED_FEE_ASSET = 'USDT';

const LEG_FIELD = {
  crypto_topup: 'crypto_top_up',
  payin: 'pay_in',
  payout: 'pay_out',
  settlement: 'settlement',
} as const;

const toNumber = (value: string | undefined): number => {
  if (value == null || value === '') {
    return 0;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const legOf = (offer: Offer, leg: OfferLegKey) => offer[LEG_FIELD[leg]];

const percentValue = (fee: Fee | undefined): number =>
  fee?.unit === 'percent' ? toNumber(fee.value) : 0;

const fixedValue = (fee: Fee | undefined): string =>
  fee?.unit === 'fixed' ? fee.value : '';

export const isLegEnabled = (offer: Offer, leg: OfferLegKey): boolean =>
  Boolean(legOf(offer, leg));

export const activeLegs = (offer: Offer): OfferLegKey[] =>
  OFFER_LEG_KEYS.filter((leg) => isLegEnabled(offer, leg));

export const legSell = (offer: Offer, leg: OfferLegKey): number =>
  percentValue(legOf(offer, leg)?.sell_rate);

export const legCost = (offer: Offer, leg: OfferLegKey): number =>
  percentValue(legOf(offer, leg)?.provider_rate);

export const legMargin = (offer: Offer, leg: OfferLegKey): number => {
  const published = legOf(offer, leg)?.margin;

  if (published) {
    return toNumber(published);
  }

  return Number((legSell(offer, leg) - legCost(offer, leg)).toFixed(2));
};

export const primaryBaseRate = (offer: Offer): number => {
  const legs = activeLegs(offer);

  if (legs.length === 0) {
    return 0;
  }

  const withRate = legs.find((leg) => legSell(offer, leg) > 0);

  return legSell(offer, withRate ?? legs[0]);
};

const trimAmount = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  const rounded = value.toFixed(8).replace(/\.?0+$/, '');

  return rounded === '-0' ? '0' : rounded;
};

export const formatLegRate = (percent: number, fixed: string): string =>
  [
    percent !== 0 ? `${percent}%` : '',
    fixed ? `${fixed} ${FIXED_FEE_ASSET}` : '',
  ]
    .filter(Boolean)
    .join(' + ');

export const formatLegMargin = (margin: number, unit: Fee['unit']): string => {
  const sign = margin > 0 ? '+' : '';

  if (unit === 'fixed') {
    return `${sign}${trimAmount(margin)} ${FIXED_FEE_ASSET}`;
  }

  return `${sign}${margin.toFixed(2)}%`;
};

export const legRateUnit = (offer: Offer, leg: OfferLegKey): Fee['unit'] => {
  const rates = legOf(offer, leg);
  const sellFixed = rates?.sell_rate.unit === 'fixed';
  const costFixed = rates?.provider_rate?.unit === 'fixed';

  if (sellFixed || costFixed) {
    return 'fixed';
  }

  return 'percent';
};

export const legSellText = (offer: Offer, leg: OfferLegKey): string => {
  const fee = legOf(offer, leg)?.sell_rate;

  if (!fee) {
    return '';
  }

  if (fee.unit === 'percent') {
    return `${fee.value}%`;
  }

  return `${fee.value} ${FIXED_FEE_ASSET}`;
};

export const legFixedSell = (offer: Offer, leg: OfferLegKey): string =>
  fixedValue(legOf(offer, leg)?.sell_rate);

export const legFixedCost = (offer: Offer, leg: OfferLegKey): string =>
  fixedValue(legOf(offer, leg)?.provider_rate);

export const parseLegKey = (value: string): OfferLegKey | undefined =>
  isOfferLegKey(value) ? value : undefined;
